import React, { useState, useEffect, useRef } from 'react';
import { CentralEdgeDevice, CentralEdgeModem, Location, SYSTEM_EXECUTION_ORG_ID } from '@simcheck/shared-types';
import { Save, MapPin, AlertCircle, Building2, Send, Plus, X, Trash2, Crosshair, RadioTower } from 'lucide-react';
import { auth, db } from '@/config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useJsApiLoader } from '@react-google-maps/api';
import { edgeDeviceService } from '../../../services/edgeDeviceService';
import { HierarchicalLocationSelector } from '../../locations/HierarchicalLocationSelector';
import { getFullHierarchicalLocations, updateLocation } from '../../../services/hierarchicalLocationService';
import { ConfirmDialog } from '@/components/ui';
import { executeFlow, getExecutionStatus } from '../../../services/runtime';
import { getFlow } from '../../../services/flowService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getDeviceTier } from '../../../hooks/useDeviceVisibility';
import { useDateFormat } from '@/hooks/useDateFormat';
import { CENTRAL_API_URL } from '@/config/env';

const DISCOVER_LOCATION_FLOW_ID = 'sys_quick_discover_location_flow';
const DISCOVER_MAPS_LIBRARIES: ('places' | 'marker')[] = ['places', 'marker'];

/**
 * A GNSS cold start runs up to ~4.5 minutes, which is far longer than anyone
 * will sit on one tab. The run itself is server-side — the flow keeps going
 * whatever the browser does — so the only thing lost on navigation was the
 * `executionId` needed to collect the answer. Park it per device so a remount
 * can rejoin the run in progress instead of stranding it.
 *
 * Per-device (not per-user): the pending run belongs to the hardware, and the
 * key has to match on whichever tab comes back to this page.
 */
const DISCOVER_PENDING_PREFIX = 'simcheck.discoverLocation:';
/** Beyond this, assume the run died with the tab rather than re-attaching to a ghost. */
const DISCOVER_PENDING_MAX_AGE_MS = 20 * 60 * 1000;

type PendingDiscovery = { executionId: string; startedAt: number };

/** Every accessor is guarded — storage throws in private mode / blocked-cookie contexts. */
function readPendingDiscovery(deviceKey: string): PendingDiscovery | null {
  if (!deviceKey) return null;
  try {
    const raw = localStorage.getItem(`${DISCOVER_PENDING_PREFIX}${deviceKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingDiscovery;
    if (!parsed?.executionId) return null;
    if (!Number.isFinite(parsed.startedAt) || Date.now() - parsed.startedAt > DISCOVER_PENDING_MAX_AGE_MS) {
      localStorage.removeItem(`${DISCOVER_PENDING_PREFIX}${deviceKey}`);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function rememberPendingDiscovery(deviceKey: string, executionId: string): void {
  if (!deviceKey) return;
  try {
    localStorage.setItem(
      `${DISCOVER_PENDING_PREFIX}${deviceKey}`,
      JSON.stringify({ executionId, startedAt: Date.now() } satisfies PendingDiscovery),
    );
  } catch {
    /* Non-fatal: the run still works, it just can't be rejoined after a remount. */
  }
}

function forgetPendingDiscovery(deviceKey: string): void {
  if (!deviceKey) return;
  try {
    localStorage.removeItem(`${DISCOVER_PENDING_PREFIX}${deviceKey}`);
  } catch {
    /* ignore */
  }
}

interface SettingsTabProps {
  device: CentralEdgeDevice;
  onUpdate?: (device: CentralEdgeDevice) => void;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({ device, onUpdate }) => {
  const { isSuperAdmin, currentUser } = useAuth();
  const fmt = useDateFormat();
  const [deviceTier, setDeviceTier] = useState<'production' | 'demo' | 'lab'>(getDeviceTier(device));
  const [address, setAddress] = useState(device.address || '');
  const [latitude, setLatitude] = useState(device.latitude?.toString() || '');
  const [longitude, setLongitude] = useState(device.longitude?.toString() || '');
  const [locationSource, setLocationSource] = useState<'manual' | 'gps' | 'network'>(
    device.locationSource || 'manual'
  );
  const [skipEsimProvisioning, setSkipEsimProvisioning] = useState(device.skipEsimProvisioning ?? false);
  const [allowOrgAdminShell, setAllowOrgAdminShell] = useState(device.allowOrgAdminShell ?? false);
  // Backhaul modem (#247): the device's own uplink. '' = none.
  const [backhaulModemImei, setBackhaulModemImei] = useState<string>(device.backhaulModemImei ?? '');
  const [backhaulApn, setBackhaulApn] = useState<string>(device.backhaulApn ?? '');
  const [modems, setModems] = useState<CentralEdgeModem[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await edgeDeviceService.getModems(device.id);
        if (!cancelled) setModems(list);
      } catch (err) {
        console.warn('Failed to load modems for backhaul selector:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [device.id]);
  const backhaulChanged =
    backhaulModemImei !== (device.backhaulModemImei ?? '') ||
    (backhaulModemImei !== '' && backhaulApn.trim() !== (device.backhaulApn ?? ''));
  const isAtOnlyModem = (m: CentralEdgeModem) =>
    /BG9[56]|BG77|BC66/i.test(String(m.model || m.modemModelName || ''));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Owner organization (super admin only) — null = shared / platform-owned
  const [ownerOrgId, setOwnerOrgId] = useState<string | null>(device.ownerOrgId ?? null);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'organizations'));
        const list = snap.docs
          .map((d) => ({ id: d.id, name: (d.data() as any)?.name || d.id }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(list);
      } catch (err) {
        console.warn('Failed to load organizations for owner selector:', err);
      }
    })();
  }, [isSuperAdmin]);
  const ownerChanged = ownerOrgId !== (device.ownerOrgId ?? null);

  // Owner-change approval gate (see handleSave). A device and the location it
  // sits at must end up with the same owner: tenants never see another org's
  // private location, so a shared device parked at a private site is invisible
  // to everyone except that site's owner — the device page looks correct while
  // no tenant can reach it. Changing the location's owner is never automatic;
  // it takes an explicit approval in the dialog below.
  const [ownerMismatchOpen, setOwnerMismatchOpen] = useState(false);
  const [alsoMoveLocation, setAlsoMoveLocation] = useState(true);

  // Discover Location state
  const [discovering, setDiscovering] = useState(false);
  const [discoverStatus, setDiscoverStatus] = useState<string | null>(null);
  // Guards the resume effect against React 18 StrictMode's double-invoke (and
  // any re-render) so one in-flight run is never polled by two loops.
  const resumedDiscoveryRef = useRef<string | null>(null);
  // Navigating away must HAND THE RUN BACK, not consume it: the old instance's
  // poll loop keeps running after unmount, and if it collects the result it
  // applies it to a dead component and clears the pending record — so
  // returning after the run finished showed nothing at all. This flag stops
  // the loop at the next tick and leaves the record for the next mount.
  const mountedRef = useRef(true);
  // Every discovery gets a generation number. Only the newest one may write to
  // the form or the status line, so a superseded run — a second click, a
  // resume that overlaps a click, a late async tick — can never reach back and
  // overwrite fields or strand a stale message.
  const discoveryGenRef = useRef(0);
  const deviceKey: string =
    device.id || (device as any).deviceId || (device as any).device_id || '';

  // Maps SDK — used for reverse geocoding after the flow returns coords.
  // Same script id as LocationsGoogleMap so the loader is shared.
  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: mapsApiKey,
    libraries: DISCOVER_MAPS_LIBRARIES,
  });

  // Location assignment state
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(device.locationId || null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Load locations on mount
  useEffect(() => {
    async function loadLocations() {
      try {
        const data = await getFullHierarchicalLocations();
        setLocations(data.locations);
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    }
    loadLocations();
  }, []);

  // Get selected location name
  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  // The location this device will sit at once the save lands.
  const targetLocation = locations.find(l => l.id === (selectedLocationId || device.locationId));
  const targetLocationOwner = targetLocation?.ownerOrgId ?? null;
  /**
   * A PRIVATE location whose owner won't match the device's new owner. A shared
   * location needs no action — it's visible to everyone — so only a private
   * mismatch strands the device.
   */
  const locationOwnerMismatch =
    !!targetLocation && targetLocationOwner !== null && targetLocationOwner !== ownerOrgId;
  const locationChanged = selectedLocationId !== (device.locationId || null);
  const orgName = (id: string | null) =>
    id === null ? 'Shared (no owner)' : orgs.find(o => o.id === id)?.name || id;

  const handleSave = async () => {
    // Stranding a device needs an explicit decision before anything is written.
    // It happens two ways and the gate used to catch only the first: re-homing
    // the DEVICE away from its location's owner, or moving it to a LOCATION
    // owned by someone else. The second is how shared devices ended up parked
    // at a private site, visible to nobody who could run on them.
    if (isSuperAdmin && (ownerChanged || locationChanged) && locationOwnerMismatch) {
      // Re-owning the location is the right default only when the DEVICE was
      // re-homed. If the operator merely reassigned it, silently taking another
      // org's location away from them is not what they asked for.
      setAlsoMoveLocation(ownerChanged);
      setOwnerMismatchOpen(true);
      return;
    }
    await performSave(false);
  };

  const performSave = async (moveLocationOwner: boolean) => {
    setSaving(true);
    setMessage(null);

    try {
      if (backhaulChanged && backhaulModemImei && !backhaulApn.trim()) {
        setMessage({ type: 'error', text: 'An APN is required for the backhaul modem.' });
        setSaving(false);
        return;
      }
      const settings: any = {
        locationSource,
        skipEsimProvisioning,
        // Only send the backhaul fields when they changed — the backend
        // validates the IMEI against known_modems on every write.
        ...(backhaulChanged && {
          backhaulModemImei: backhaulModemImei || null,
          backhaulApn: backhaulModemImei ? backhaulApn.trim() : null,
        }),
        ...(isSuperAdmin && { deviceTier, allowOrgAdminShell }),
        // Only send ownerOrgId when it actually changed — the backend gates
        // this field to super admins.
        ...(isSuperAdmin && ownerChanged && { ownerOrgId }),
      };

      if (address.trim()) {
        settings.address = address.trim();
      }

      if (latitude.trim() && longitude.trim()) {
        settings.latitude = parseFloat(latitude);
        settings.longitude = parseFloat(longitude);
      }

      // Include location assignment
      if (selectedLocationId !== device.locationId) {
        settings.locationId = selectedLocationId;
        settings.locationName = selectedLocation?.displayName || selectedLocation?.name || null;
        settings.countryId = selectedLocation?.countryId || null;
        settings.regionId = selectedLocation?.regionId || null;
      }

      await edgeDeviceService.updateDeviceSettings(device.id, settings);

      // Approved in the dialog: bring the location's ownership in line with the
      // device's, so the device is actually reachable by the tenants that can
      // now see it. Shared device → shared location.
      if (moveLocationOwner && targetLocation) {
        await updateLocation(targetLocation.id, {
          ownerOrgId,
          isPublic: ownerOrgId === null,
          organizationIds: ownerOrgId ? [ownerOrgId] : [],
        } as Partial<Location>);
      }

      // skipEsimProvisioning is persisted by central-server on `devices/{id}`
      // (the PATCH above). It used to be mirrored client-side into the legacy
      // `edge_devices` collection; that collection is no longer client-writable.

      setMessage({
        type: 'success',
        text: moveLocationOwner && targetLocation
          ? `Settings saved. "${targetLocation.displayName || targetLocation.name}" is now ${orgName(ownerOrgId)}.`
          : 'Settings saved successfully!',
      });

      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate({
          ...device,
          ...settings,
          locationUpdatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to save settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Discover the device's physical location by running a normal flow against
   * one of its GNSS-capable modems. The flow polls GPS first (up to ~4.5 min,
   * exiting the moment it locks), then captures serving-cell info as fallback.
   * The frontend reads the coords (or cell info) from the execution outputs,
   * reverse-geocodes to a street address via Google Maps, and pre-fills the
   * form fields. Save still requires the existing Save button.
   *
   * This only STARTS the run; `collectDiscoveryResult` applies the answer, so
   * navigating away mid-discovery loses nothing.
   */
  const handleDiscoverLocation = async () => {
    setMessage(null);
    setDiscoverStatus(null);

    const targetLocationId = selectedLocationId || device.locationId;
    if (!targetLocationId) {
      setMessage({ type: 'error', text: 'Assign a location first — the flow needs a location to allocate a modem.' });
      return;
    }
    // Only the user is required. Attribution follows the DEVICE (see execOrgId
    // below), so a super admin browsing with no tenant selected is a perfectly
    // valid caller — requiring `activeOrganizationId` here blocked exactly the
    // operator this button exists for.
    if (!currentUser) {
      setMessage({ type: 'error', text: 'No active user context.' });
      return;
    }
    if (!mapsApiKey) {
      setMessage({ type: 'error', text: 'VITE_GOOGLE_MAPS_API_KEY is not configured — cannot reverse-geocode.' });
      return;
    }

    setDiscovering(true);
    let startedExecutionId: string;
    try {
      setDiscoverStatus('Allocating a GNSS-capable modem on this device…');

      // Attribution follows the DEVICE, not whoever is looking at it (#267).
      // Stamping `activeOrganizationId` filed super-admin discover clicks on
      // shared devices under whichever tenant happened to be selected, and the
      // failures then sat in that customer's dashboard forever — naming another
      // tenant's location id. A tenant admin discovering their own device still
      // gets their own org here (ownerOrgId === activeOrganizationId), so the
      // allocator's tenancy gates behave exactly as before; only the shared-
      // device case changes, and that path is super-admin-only (writes on
      // shared devices are `canWriteDevice` = super-admin), whose allocator
      // gates are skipped via isSuperAdmin.
      const execOrgId = device.ownerOrgId || SYSTEM_EXECUTION_ORG_ID;

      // `sys_` flows skip the tenant-isolation check in getFlow, so the device's
      // org is a safe (and more honest) argument than whatever tenant is selected.
      const flowDef = await getFlow(DISCOVER_LOCATION_FLOW_ID, execOrgId);
      if (!flowDef) {
        throw new Error(`System flow ${DISCOVER_LOCATION_FLOW_ID} not found — re-run the systemFlowSeed.`);
      }

      const { executionId } = await executeFlow(
        flowDef,
        {},
        currentUser.uid,
        execOrgId,
        { forcedDeviceId: deviceKey },
        targetLocationId,
        false,
        undefined,
        'admin-discover-location'
      );
      startedExecutionId = executionId;
      // Persist BEFORE the first await on the result: from here on the run is
      // recoverable even if the tab closes a millisecond later.
      rememberPendingDiscovery(deviceKey, executionId);
      resumedDiscoveryRef.current = executionId;
    } catch (err: any) {
      console.error('Discover location failed to start:', err);
      setMessage({ type: 'error', text: err?.message || 'Failed to start location discovery' });
      setDiscovering(false);
      setDiscoverStatus(null);
      return;
    }

    await collectDiscoveryResult(startedExecutionId);
  };

  /**
   * Poll an execution to completion, abandoning the wait (without consuming the
   * run) as soon as this component unmounts. Replaces `pollExecutionStatus`,
   * whose setInterval can't be cancelled from here.
   *
   * Returns null when the caller navigated away — the pending record must then
   * survive so the next mount can pick the run back up.
   */
  const waitForExecution = async (
    executionId: string,
    onTick?: (status: any) => void,
    alive: () => boolean = () => mountedRef.current,
  ): Promise<any | null> => {
    const deadline = Date.now() + 10 * 60 * 1000;
    while (Date.now() < deadline) {
      if (!alive()) return null;
      const status: any = await getExecutionStatus(executionId);
      if (!alive()) return null;
      onTick?.(status);
      if (status?.status === 'completed' || status?.status === 'failed' || status?.status === 'cancelled') {
        return status;
      }
      await new Promise((r) => setTimeout(r, 5000));
    }
    throw new Error('Execution polling timeout');
  };

  /**
   * Reverse-geocode to a street address. Best-effort — the coordinates stand on
   * their own — but it waits for the Maps SDK rather than giving up on it. The
   * cell position now arrives ~1 s after the click, which can beat the script:
   * bailing immediately left the Address blank with coordinates filled in, and
   * a run that never gets a GPS fix never revisited it.
   */
  const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
    const deadline = Date.now() + 10000;
    while (!(window as any).google?.maps?.Geocoder && Date.now() < deadline) {
      if (!mountedRef.current) return null;
      await new Promise((r) => setTimeout(r, 250));
    }
    if (!(window as any).google?.maps?.Geocoder) {
      console.warn('Reverse geocode skipped: Maps SDK never became available');
      return null;
    }
    try {
      const geocoder = new (window as any).google.maps.Geocoder();
      const result: any = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
          if (status === 'OK' && results && results[0]) resolve(results[0]);
          else reject(new Error(`Geocoder status: ${status}`));
        });
      });
      return result?.formatted_address || null;
    } catch (err) {
      console.warn('Reverse geocode failed:', err);
      return null;
    }
  };

  /**
   * Fill the form from a resolved position and record how it was obtained.
   * Never saves — persisting stays an explicit click, so a coarse cell estimate
   * that a later GPS fix improves on is only ever a draft.
   */
  const applyDiscoveredPosition = async (
    lat: number,
    lng: number,
    source: 'gps' | 'network',
  ): Promise<string | null> => {
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
    setLocationSource(source);
    const resolvedAddress = await reverseGeocode(lat, lng);
    if (resolvedAddress) setAddress(resolvedAddress);
    return resolvedAddress;
  };

  /**
   * Resolve the serving cell to a position via Google's Geolocation API.
   * Returns the coordinates, or the reason it could not — the caller decides
   * whether that reason is fatal (no GPS either) or just a missed head start.
   */
  const geolocateFromCell = async (
    o: Record<string, any>,
  ): Promise<{ lat: number; lng: number } | { error: string }> => {
    const mcc = o?.mcc ? parseInt(o.mcc, 10) : NaN;
    const mnc = o?.mnc ? parseInt(o.mnc, 10) : NaN;
    const cellIdHex: string | undefined = o?.cellId;
    const tacHex: string | undefined = o?.tac;
    const radioType: string = (o?.radioType || '').toLowerCase() || 'lte';

    if (!Number.isFinite(mcc) || !Number.isFinite(mnc) || !cellIdHex) {
      return {
        error:
          'No GPS fix and the modem returned no usable cell info ' +
          '(mcc=' + (o?.mcc ?? 'none') +
          ', mnc=' + (o?.mnc ?? 'none') +
          ', cellId=' + (o?.cellId ?? 'none') + '). ' +
          'LTE, WCDMA and GSM serving cells are all parsed, so this usually means ' +
          'the modem is not camped on any cell — or is on a RAT (NR SA) the primitive does not read yet.',
      };
    }

    const cellId = parseInt(cellIdHex, 16);
    const lac = tacHex ? parseInt(tacHex, 16) : undefined;
    // `radioType` is a TOP-LEVEL request field. Google's cell-tower object has
    // no such key, so nesting it there (as this did until 2026-09-19) meant it
    // was silently dropped and the request fell back to the default `gsm` —
    // under which a 28-bit LTE ECI is out of range and Google answers 404
    // notFound "without any indicator in the response" (their troubleshooting
    // page). Every cell lookup failed this way regardless of carrier or
    // country; moving the field out resolved the same three cells (US/UK/TH)
    // that had been 404ing.
    const googleRadioType = radioType === 'nr5g' || radioType === 'nr' ? 'nr' : radioType;
    const geoBody = {
      considerIp: false,
      radioType: googleRadioType,
      cellTowers: [{
        // NR reports the cell under a different key (a 36-bit NCI won't fit
        // `cellId`'s 28-bit ECI range).
        ...(googleRadioType === 'nr' ? { newRadioCellId: cellId } : { cellId }),
        ...(lac !== undefined && Number.isFinite(lac) ? { locationAreaCode: lac } : {}),
        mobileCountryCode: mcc,
        mobileNetworkCode: mnc,
      }],
    };
    console.log('[discover-location] geolocate request body:', geoBody);
    const geoRes = await fetch(
      `https://www.googleapis.com/geolocation/v1/geolocate?key=${encodeURIComponent(mapsApiKey)}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(geoBody) }
    );
    const geoBodyText = await geoRes.text();
    console.log('[discover-location] geolocate response status=' + geoRes.status, geoBodyText);

    if (geoRes.ok) {
      try {
        const geoData = JSON.parse(geoBodyText);
        if (geoData?.location?.lat != null && geoData?.location?.lng != null) {
          return { lat: geoData.location.lat, lng: geoData.location.lng };
        }
        return { error: 'Cell-tower geolocation returned 200 but no location field: ' + geoBodyText.slice(0, 200) };
      } catch {
        return { error: 'Cell-tower geolocation returned non-JSON body: ' + geoBodyText.slice(0, 200) };
      }
    }
    if (geoRes.status === 403) {
      return {
        error:
          'Google Geolocation API returned 403. The Geolocation API is a separate product from Maps Geocoding — ' +
          'enable it at https://console.cloud.google.com/apis/library/geolocation.googleapis.com and make sure ' +
          "VITE_GOOGLE_MAPS_API_KEY's API restriction allows it. Body: " + geoBodyText.slice(0, 200),
      };
    }
    if (geoRes.status === 404) {
      return {
        error:
          'Google has no record of cell mcc=' + mcc + ' mnc=' + mnc + ' cellId=' + cellId + ' (404 notFound). ' +
          "The cell is real but isn't in Google's crowdsourced DB — try outdoors for GPS or enter manually.",
      };
    }
    return { error: 'Cell-tower geolocation HTTP ' + geoRes.status + ': ' + geoBodyText.slice(0, 200) };
  };

  /**
   * Wait on an already-running discovery and apply its result. Split out from
   * the starter so a remount can rejoin a run it didn't start — the only
   * difference between the two callers is who created the execution.
   *
   * Two phases, because the two sources answer on completely different
   * timescales: the serving cell resolves in well under a second and is
   * mirrored onto the execution doc mid-run (`partialOutputs`), so the fields
   * get a usable position almost immediately; the GNSS fix may take minutes and
   * simply overwrites it when it lands. Nothing is persisted either way until
   * Save, so the coarse estimate is never more than a draft.
   */
  const collectDiscoveryResult = async (executionId: string, opts: { resumed?: boolean } = {}) => {
    const gen = ++discoveryGenRef.current;
    /** This run is still the one the UI belongs to. */
    const current = () => mountedRef.current && discoveryGenRef.current === gen;
    setDiscovering(true);
    let cellLookupStarted = false;
    let haveCellPosition = false;
    let cellPosition: { lat: number; lng: number } | null = null;
    let cellAddress: string | null = null;
    // Handle on the mid-run cell lookup. It MUST be awaited before the run's
    // result is interpreted: it was fire-and-forget, so a run that finished
    // while the lookup was still in flight saw `haveCellPosition === false`,
    // took the "never got a cell position" branch, geolocated a second time,
    // and then had the late task re-apply the position and re-set the status
    // line — after `finally` had already cleared it, so it stuck on screen.
    let cellLookup: Promise<void> | null = null;
    try {
      setDiscoverStatus(
        opts.resumed
          ? 'Rejoined a discovery already running on this device…'
          : 'Reading the serving cell, then waiting for a GPS fix. You can leave this page.',
      );

      // 5 s, not 2 s: a wait measured in minutes gains nothing from 2 s
      // granularity, and each poll reloads the full execution doc server-side
      // (see the memory note on getExecutionStatus) — this cuts that load 2.5x.
      const finalStatus = await waitForExecution(
        executionId,
        (status) => {
          // Fire once, the moment the cell read shows up mid-run.
          if (cellLookupStarted) return;
          const partial = (status as any)?.partialOutputs;
          if (!partial?.cellId) return;
          cellLookupStarted = true;
          cellLookup = (async () => {
            const cell = await geolocateFromCell(partial);
            if ('error' in cell) {
              // Not fatal here — GPS is still running and is the better answer.
              console.warn('[discover-location] cell pre-fill failed:', cell.error);
              return;
            }
            haveCellPosition = true;
            cellPosition = { lat: cell.lat, lng: cell.lng };
            if (!current()) return;
            cellAddress = await applyDiscoveredPosition(cell.lat, cell.lng, 'network');
            if (!current()) return;
            setDiscoverStatus(
              'Approximate position from the serving cell — still waiting for a GPS fix (up to ~4.5 min). You can leave this page.',
            );
          })();
        },
        current,
      );

      // Unmounted mid-wait: leave the pending record alone so the next visit to
      // this device rejoins the run (and collects a result that landed while we
      // were away) instead of finding nothing.
      if (!finalStatus) return;

      // Settle the mid-run lookup before reading `haveCellPosition`, so the
      // branches below see the truth rather than a race.
      if (cellLookup) {
        try {
          await cellLookup;
        } catch (e) {
          console.warn('[discover-location] cell lookup task failed:', e);
        }
      }

      if (finalStatus.status !== 'completed') {
        const errMsg = (finalStatus as any)?.error || (finalStatus as any)?.result?.error || 'Flow did not complete';
        throw new Error(`Location discovery failed: ${errMsg}`);
      }

      // Outputs are surfaced on the execution doc under either `outputs` or
      // `result.outputs` depending on which path the runtime took.
      const outputs: Record<string, any> =
        (finalStatus as any)?.outputs ||
        (finalStatus as any)?.result?.outputs ||
        {};

      console.log('[discover-location] flow outputs:', outputs);

      const gpsLat = parseFloat(outputs.latitude);
      const gpsLng = parseFloat(outputs.longitude);
      if (Number.isFinite(gpsLat) && Number.isFinite(gpsLng)) {
        const resolvedAddress = await applyDiscoveredPosition(gpsLat, gpsLng, 'gps');
        setMessage({
          type: 'success',
          text:
            `GPS fix obtained${haveCellPosition ? ' — replaced the cell-tower estimate' : ''}. Click Save to persist.` +
            (resolvedAddress ? '' : ' (Reverse geocoding skipped — address not auto-filled.)'),
        });
        return;
      }

      // No fix. Whatever the cell gave us mid-run stands as the answer — but if
      // its reverse geocode lost the race with the Maps SDK, this is the last
      // chance to fill the Address, since nothing else will revisit it.
      if (haveCellPosition) {
        let addressNote = '';
        // Assigned inside the poll callback, which TS can't see — narrow by hand.
        const pos = cellPosition as { lat: number; lng: number } | null;
        if (!cellAddress && pos) {
          const retried = await reverseGeocode(pos.lat, pos.lng);
          if (retried) setAddress(retried);
          else addressNote = ' (Address could not be resolved — enter it manually.)';
        }
        setMessage({
          type: 'success',
          text:
            'No GPS fix — keeping the cell-tower position (coarser accuracy). Click Save to persist.' +
            addressNote,
        });
        return;
      }

      // The mid-run mirror never arrived (an older run, or the lookup raced the
      // finish). Try once more from the final outputs before giving up.
      const cell = await geolocateFromCell(outputs);
      if ('error' in cell) throw new Error(cell.error);
      const resolvedAddress = await applyDiscoveredPosition(cell.lat, cell.lng, 'network');
      setMessage({
        type: 'success',
        text:
          'No GPS fix; used cell-tower geolocation (coarser accuracy). Click Save to persist.' +
          (resolvedAddress ? '' : ' (Reverse geocoding skipped — address not auto-filled.)'),
      });
    } catch (err: any) {
      if (!current()) return;
      console.error('Discover location failed:', err);
      setMessage({ type: 'error', text: err?.message || 'Failed to discover location' });
    } finally {
      // Only clear when THIS run consumed it to a terminal state. An unmounted
      // or superseded run hands it back instead.
      if (current()) {
        forgetPendingDiscovery(deviceKey);
        setDiscovering(false);
        setDiscoverStatus(null);
      }
    }
  };

  /**
   * Rejoin a discovery left running by a previous visit to this page.
   * `pollExecutionStatus` returns immediately when the run already finished, so
   * coming back late collects the result rather than waiting again.
   */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!deviceKey) return;
    const pending = readPendingDiscovery(deviceKey);
    if (!pending) return;
    if (resumedDiscoveryRef.current === pending.executionId) return;
    resumedDiscoveryRef.current = pending.executionId;
    void collectDiscoveryResult(pending.executionId, { resumed: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey]);

  return (
    <div className="max-w-3xl">
      <div className="bg-card border border-border dark:border-border-subtle rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Device Settings</h3>

        {/* Device Identity Info */}
        <div className="bg-accent-blue-faded border border-border rounded p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-accent-blue mt-0.5" />
            <div className="text-sm text-accent-blue">
              <p className="font-medium mb-1">Device Identity</p>
              <p>
                Device ID is provided by the edge device and cannot be changed: 
                <span className="font-mono ml-1">{device.deviceId || device.device_id}</span>
              </p>
              {device.edge_provided_name && (
                <p className="mt-1">
                  Edge-provided name: <span className="font-semibold">{device.edge_provided_name}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Hierarchical Location Assignment */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Assigned Location
          </label>
          <div className="max-w-md">
            <HierarchicalLocationSelector
              selectedLocationId={selectedLocationId}
              onSelectLocation={setSelectedLocationId}
              disabled={loadingLocations}
            />
          </div>
          {locationOwnerMismatch && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-status-warning/30 bg-status-warning-bg/40 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" />
              {/* Deliberately names no organization: `orgs` is only loaded for
                  super admins, so anyone else would read a raw org id here. */}
              <p className="text-sm text-foreground">
                <span className="font-medium">
                  {ownerOrgId === null
                    ? 'A shared device at a private location.'
                    : 'This device and its location belong to different organizations.'}
                </span>{' '}
                Tenants never see another organization's private location, so this device stays
                invisible to everyone outside the organization that owns the location — it reads as
                correctly assigned while nobody can run tests on it. Pick a shared location, or
                change who owns this one.
              </p>
            </div>
          )}
          {device.locationName && selectedLocationId === device.locationId && (
            <p className="text-sm text-muted-foreground mt-2">
              Currently assigned to: <span className="font-medium">{device.locationName}</span>
            </p>
          )}
          {selectedLocationId && selectedLocationId !== device.locationId && (
            <p className="text-sm text-status-warning mt-2">
              Will be reassigned to: <span className="font-medium">
                {selectedLocation?.displayName || selectedLocation?.name || 'Unknown'}
              </span>
            </p>
          )}
          {!selectedLocationId && device.locationId && (
            <p className="text-sm text-status-warning mt-2">
              Will be removed from location assignment
            </p>
          )}
        </div>

        {/* Location Source was a three-way radio (Manual / GPS from Modem /
            Network-based) that the operator had to set by hand — but nothing
            read it as an instruction: it never chose how the coordinates were
            obtained, it only labelled them after the fact, and Discover
            Location overwrote whatever was selected. Removed as a control and
            kept as the derived provenance line below, which is the only thing
            it was ever really saying. The `locationSource` field is still
            written on save. */}
        <div className="mb-6">
          {/* Discover Location — runs a normal flow on this device's GNSS-capable
              modem: serving cell first for an immediate coarse position, then a
              GPS fix that supersedes it. Save still requires a separate click. */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDiscoverLocation}
              disabled={discovering || !(selectedLocationId || device.locationId)}
              title={!(selectedLocationId || device.locationId) ? 'Assign a location first' : 'Run a GPS read on this device (with cell-tower fallback)'}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {discovering ? (
                <>
                  <span className="w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                  <span>Discovering…</span>
                </>
              ) : (
                <>
                  <Crosshair className="w-4 h-4" />
                  <span>Discover Location</span>
                </>
              )}
            </button>
            {discoverStatus && (
              <span className="text-xs text-muted-foreground">{discoverStatus}</span>
            )}
          </div>
          {/* Provenance, derived — replaces the old radio group. Only shown
              once something has actually produced coordinates. */}
          {locationSource !== 'manual' && (latitude || longitude) && (
            <p className="mt-2 text-xs text-muted-foreground">
              {locationSource === 'gps'
                ? 'Coordinates from a GPS fix on this device.'
                : 'Approximate coordinates from the serving cell tower — typically accurate to a few hundred metres.'}
            </p>
          )}
        </div>

        {/* Location fields — always editable. Discover Location pre-fills them
            (cell estimate first, GPS fix when it lands) and stamps
            `locationSource`; the admin can still tweak the result before
            saving, and typing here marks it manual. */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Address
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
              <input
                type="text"
                value={address}
                onChange={(e) => { setAddress(e.target.value); setLocationSource('manual'); }}
                placeholder="Enter device location address..."
                className="w-full pl-10 pr-3 py-2 border border-border rounded focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Latitude
              </label>
              <input
                type="text"
                value={latitude}
                onChange={(e) => { setLatitude(e.target.value); setLocationSource('manual'); }}
                placeholder="e.g., 52.5200"
                className="w-full px-3 py-2 border border-border rounded font-mono focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Longitude
              </label>
              <input
                type="text"
                value={longitude}
                onChange={(e) => { setLongitude(e.target.value); setLocationSource('manual'); }}
                placeholder="e.g., 13.4050"
                className="w-full px-3 py-2 border border-border rounded font-mono focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Current Location Display */}
        {(device.latitude || device.longitude || device.address) && (
          <div className="bg-muted border border-border dark:border-border-subtle rounded p-4 mb-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Current Location</p>
            {device.address && (
              <p className="text-sm text-foreground">{device.address}</p>
            )}
            {device.latitude && device.longitude && (
              <p className="text-sm text-foreground font-mono">
                {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
              </p>
            )}
            {device.locationUpdatedAt && (
              <p className="text-xs text-muted-foreground/70 mt-2">
                Last updated: {fmt.formatDateTime(device.locationUpdatedAt)}
              </p>
            )}
          </div>
        )}

        {/* Success/Error Message */}
        {message && (
          <div
            className={`
              rounded p-4 mb-6
              ${message.type === 'success'
                ? 'bg-status-success-bg border border-status-success text-status-success'
                : 'bg-status-error-bg border border-status-error text-status-error'
              }
            `}
          >
            {message.text}
          </div>
        )}

        {/* Device Tier (super admin only) */}
        {isSuperAdmin && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              Device Tier
            </label>
            <select
              value={deviceTier}
              onChange={(e) => setDeviceTier(e.target.value as 'production' | 'demo' | 'lab')}
              className="w-full max-w-xs px-3 py-2 bg-card border border-border rounded text-sm text-foreground focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            >
              <option value="production">Production</option>
              <option value="demo">Demo</option>
              <option value="lab">Lab</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1.5">
              Controls which tenants can see this device. Production devices are visible to all.
              Demo devices are only visible to demo orgs. Lab devices require lab access.
            </p>
          </div>
        )}

        {/* Owner Organization (super admin only) */}
        {isSuperAdmin && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Owner Organization
            </label>
            <select
              value={ownerOrgId ?? '__shared__'}
              onChange={(e) => setOwnerOrgId(e.target.value === '__shared__' ? null : e.target.value)}
              className="w-full max-w-xs px-3 py-2 bg-card border border-border rounded text-sm text-foreground focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            >
              <option value="__shared__">Shared (no owner)</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1.5">
              Private devices are visible and manageable only by the owning organization
              (plus super admins). Shared devices belong to the platform commons.
            </p>
            {ownerChanged && (
              <p className="text-xs text-status-warning mt-1.5">
                Ownership will change on save — this affects who can see and manage the
                device.
                {locationOwnerMismatch
                  ? ` Its location "${targetLocation?.displayName || targetLocation?.name}" is private to ${orgName(targetLocationOwner)}; you'll be asked what to do with it on save.`
                  : ' Its location assignment is not moved automatically.'}
              </p>
            )}
          </div>
        )}

        {/* Interactive Shell grant (super admin only) */}
        {isSuperAdmin && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              Interactive Shell
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={allowOrgAdminShell}
                onChange={(e) => setAllowOrgAdminShell(e.target.checked)}
                className="w-4 h-4 text-accent-blue focus:ring-accent-blue rounded"
              />
              <div>
                <span className="text-sm text-foreground">Allow org owners/admins to open a Terminal Session</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When checked, owners and admins of this device's owning organization can open
                  an interactive shell on the Remote tab. Off by default — super admin only.
                  All I/O is logged. Sessions are still capped at 30 min / 10 min idle.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* eSIM Provisioning override */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-muted-foreground mb-3">
            eSIM Provisioning
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={skipEsimProvisioning}
              onChange={(e) => setSkipEsimProvisioning(e.target.checked)}
              className="w-4 h-4 text-accent-blue focus:ring-accent-blue rounded"
            />
            <div>
              <span className="text-sm text-foreground">Skip eSIM provisioning on this device</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Hard override — when checked, flow executions skip eSIM provisioning entirely
                regardless of the per-modem method (SGP.32 / lpac / manual) set on the Modems tab.
                Uncheck to let each modem use its own configured method (with org default fallback).
              </p>
            </div>
          </label>
        </div>

        {/* Backhaul modem (#247) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <RadioTower className="w-4 h-4" />
            Backhaul Modem
          </label>
          <div className="flex flex-wrap gap-3">
            <select
              value={backhaulModemImei}
              onChange={(e) => setBackhaulModemImei(e.target.value)}
              className="w-full max-w-xs px-3 py-2 bg-card border border-border rounded text-sm text-foreground focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            >
              <option value="">None (WiFi / Ethernet only)</option>
              {modems.map((m) => {
                const imei = m.imei || m.modemId || '';
                if (!imei) return null;
                const atOnly = isAtOnlyModem(m);
                const label = `${m.modemModelName || m.model || 'Modem'} — ${imei}`;
                return (
                  <option key={imei} value={imei} disabled={atOnly || m.disabled === true}>
                    {label}{atOnly ? ' (AT-only, unsupported)' : m.disabled ? ' (disabled)' : ''}
                  </option>
                );
              })}
            </select>
            <input
              type="text"
              value={backhaulApn}
              onChange={(e) => setBackhaulApn(e.target.value)}
              placeholder="APN (e.g. internet)"
              disabled={!backhaulModemImei}
              className="w-full max-w-[14rem] px-3 py-2 bg-card border border-border rounded text-sm text-foreground font-mono focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:opacity-50"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            The backhaul modem carries this device's own traffic (console link, updates) as a
            failover uplink when no WiFi or Ethernet is available. It is never used for tests.
            Its eSIM can only be changed through the guided uplink switch on the Modems tab.
          </p>
          {device.backhaulModemImei && (
            <p className="text-xs mt-1.5">
              {device.backhaulStatus?.active ? (
                <span className="text-status-success">
                  Bearer up{device.backhaulStatus.iface ? ` on ${device.backhaulStatus.iface}` : ''}
                  {device.backhaulStatus.ip ? ` (${device.backhaulStatus.ip})` : ''}
                  {device.backhaulStatus.isDefaultRoute ? ' — carrying host traffic' : ' — standby behind WiFi/Ethernet'}
                </span>
              ) : device.backhaulStatus ? (
                <span className="text-status-error">
                  Bearer down{device.backhaulStatus.lastError ? ` — ${device.backhaulStatus.lastError}` : ''}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  No status reported yet — the device needs an edge release with backhaul support.
                </span>
              )}
            </p>
          )}
          {backhaulChanged && (
            <p className="text-xs text-status-warning mt-1.5">
              {backhaulModemImei
                ? 'This modem will be removed from test allocation on save.'
                : 'Clearing the backhaul — the device will rely on WiFi/Ethernet only.'}
            </p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-accent-blue text-white rounded hover:bg-accent-blue/80 disabled:bg-muted disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Settings</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Remote Configuration */}
      <RemoteConfigSection deviceId={device.id || device.deviceId || device.device_id} />

      {/* Danger Zone */}
      <DeleteDeviceSection device={device} />

      {/*
        Owner change vs location owner. Approval is explicit: the location is
        only re-owned if the box below is ticked, and it stays ticked by default
        because leaving them mismatched is what makes a shared device
        unreachable for every tenant.
      */}
      <ConfirmDialog
        open={ownerMismatchOpen}
        onClose={() => setOwnerMismatchOpen(false)}
        onConfirm={async () => {
          setOwnerMismatchOpen(false);
          await performSave(alsoMoveLocation);
        }}
        title="Location owner doesn't match"
        variant="warning"
        confirmText="Save changes"
        description={
          <div className="space-y-3">
            <p>
              This device {ownerChanged ? 'is becoming' : 'is'}{' '}
              <strong>{orgName(ownerOrgId)}</strong>, but{' '}
              {locationChanged ? 'the location you picked' : 'its location'},{' '}
              <strong>{targetLocation?.displayName || targetLocation?.name}</strong>, is private to{' '}
              <strong>{orgName(targetLocationOwner)}</strong>.
            </p>
            <p>
              Tenants never see another organization's private location, so the device would stay
              invisible to them — it would look correctly assigned here while no one could run
              tests on it.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={alsoMoveLocation}
                onChange={(e) => setAlsoMoveLocation(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-accent-blue focus:ring-accent-blue rounded"
              />
              <span className="text-sm">
                Also change the location's owner to <strong>{orgName(ownerOrgId)}</strong>
                {targetLocationOwner !== null && (
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {orgName(targetLocationOwner)} loses management of this location, and any of
                    their other devices assigned to it move with it.
                  </span>
                )}
              </span>
            </label>
          </div>
        }
      />
    </div>
  );
};

// Delete Device Section
function DeleteDeviceSection({ device }: { device: CentralEdgeDevice }) {
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const deviceName = device.name || device.deviceId || device.device_id;
  const canDelete = confirmText === deviceName;

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      await edgeDeviceService.deleteDevice(device.id);
      navigate('/admin/edge-devices');
    } catch (error) {
      console.error('Failed to delete device:', error);
      setDeleting(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-destructive/30 p-6 mt-6">
      <h3 className="text-lg font-semibold text-destructive mb-2">Danger Zone</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Permanently remove this device and all its associated data. This action cannot be undone.
      </p>

      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="flex items-center gap-2 px-4 py-2 border border-destructive text-destructive rounded-md text-sm font-medium hover:bg-destructive hover:text-white transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete This Device
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-foreground">
            Type <span className="font-mono font-bold text-destructive">{deviceName}</span> to confirm:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={deviceName}
            className="w-full max-w-md px-3 py-2 bg-muted border border-border rounded text-sm text-foreground font-mono"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={!canDelete || deleting}
              className="flex items-center gap-2 px-4 py-2 bg-destructive text-white rounded-md text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleting ? (
                <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4" /> Permanently Delete</>
              )}
            </button>
            <button
              onClick={() => { setShowConfirm(false); setConfirmText(''); }}
              className="px-4 py-2 border border-border text-muted-foreground rounded-md text-sm hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Remote Configuration Push Section
function RemoteConfigSection({ deviceId }: { deviceId: string }) {
  const [entries, setEntries] = useState<{ key: string; value: string }[]>([
    { key: 'LOG_LEVEL', value: 'INFO' },
  ]);
  const [requiresRestart, setRequiresRestart] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [configMsg, setConfigMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const addEntry = () => setEntries([...entries, { key: '', value: '' }]);
  const removeEntry = (i: number) => setEntries(entries.filter((_, idx) => idx !== i));
  const updateEntry = (i: number, field: 'key' | 'value', val: string) => {
    const updated = [...entries];
    updated[i][field] = val;
    setEntries(updated);
  };

  const handlePush = async () => {
    const config: Record<string, string> = {};
    for (const e of entries) {
      if (e.key.trim()) config[e.key.trim()] = e.value;
    }
    if (Object.keys(config).length === 0) return;

    setPushing(true);
    setConfigMsg(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const apiUrl = CENTRAL_API_URL;

      const res = await fetch(`${apiUrl}/devices/${deviceId}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ config, requiresRestart }),
      });

      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      const data = await res.json();
      setConfigMsg({
        type: 'success',
        text: `Config pushed${data.deliveredViaWebSocket ? ' (delivered via WebSocket)' : ' (queued for updater)'}`,
      });
    } catch (e: any) {
      setConfigMsg({ type: 'error', text: e.message || 'Failed to push config' });
    }
    setPushing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mt-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Remote Configuration</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Push configuration changes to this device. Changes are written to production.env on the device.
      </p>

      <div className="space-y-2 mb-4">
        {entries.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={entry.key}
              onChange={e => updateEntry(i, 'key', e.target.value)}
              placeholder="KEY"
              className="w-1/3 px-3 py-2 bg-muted border border-border rounded text-sm text-foreground font-mono"
            />
            <span className="text-muted-foreground">=</span>
            <input
              value={entry.value}
              onChange={e => updateEntry(i, 'value', e.target.value)}
              placeholder="value"
              className="flex-1 px-3 py-2 bg-muted border border-border rounded text-sm text-foreground"
            />
            <button onClick={() => removeEntry(i)} className="text-muted-foreground hover:text-destructive p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button onClick={addEntry} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <Plus className="w-3 h-3" /> Add Key
      </button>

      <label className="flex items-center gap-2 text-sm text-foreground mb-4">
        <input type="checkbox" checked={requiresRestart} onChange={e => setRequiresRestart(e.target.checked)} className="rounded" />
        Requires service restart
      </label>

      {configMsg && (
        <div className={`text-sm mb-4 p-2 rounded ${configMsg.type === 'success' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-destructive/10 text-destructive'}`}>
          {configMsg.text}
        </div>
      )}

      <button
        onClick={handlePush}
        disabled={pushing || entries.every(e => !e.key.trim())}
        className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-md text-sm font-medium hover:bg-accent-blue/90 disabled:opacity-50"
      >
        {pushing ? (
          <><span className="animate-spin">⏳</span> Pushing...</>
        ) : (
          <><Send className="w-4 h-4" /> Push Config to Device</>
        )}
      </button>
    </div>
  );
}

